import { Component, computed, effect, EffectRef, ElementRef, inject, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { Configuration } from '../../../../models/interfaces/configuration/index.interface';
import { APP_CONFIG } from '../../../../configuration/configuration';
import { KeyValue, KeyValuePipe } from '@angular/common';
import { AppViews } from '../../../../models/enums/dvm/DvmViews.enum';
import { DvmService } from '../../../../services/dvm.service';
import { Texts } from '@models/interfaces/components/Texts.interface';
import anime from 'animejs';

export const PRESERVED_TEXT_CHARACTERS = ['ß', 'ẞ', 'Ø', 'ø'] as const;
const PRESERVED_TEXT_CHARACTERS_SET = new Set<string>(PRESERVED_TEXT_CHARACTERS);

@Component({
    selector: 'app-event-title',
    imports: [KeyValuePipe],
    templateUrl: './event-title.component.html',
    styleUrl: './event-title.component.css'
})
export class EventTitleComponent {

  // VIEWCHILD
  @ViewChild('eventTitle') 
  public eventTitle!:           ElementRef<HTMLDivElement>;

  // SERVICES
  private readonly config:      Signal<Configuration>  = inject(APP_CONFIG);
  private readonly view:        Signal<AppViews| null> = inject(DvmService).view;


  // STATE
  public readonly  texts:       Signal<Texts>          = computed((): Texts => this.config().General.texts);
  public readonly  displayTexts: Signal<Texts>          = computed((): Texts => {
    const texts: Texts = this.texts();

    return {
      top: this.formatDisplayText(texts.top),
      main: this.formatDisplayText(texts.main),
      bottom: this.formatDisplayText(texts.bottom),
    };
  });
  public readonly  hideElement: WritableSignal<boolean> = signal(true);

  // Gate por clase, no `@if`: recrear el div dejaria `opacity-0` sin reanimar (`viewChange` solo depende de `view()`).
  public readonly  showTitles:  Signal<boolean>        = computed((): boolean => this.config().Ui.TitlesDisplay.enabled);


  // EFFECTS
  public readonly  viewChange:  EffectRef              = effect((): void => {

    const view: AppViews | null = this.view();

    if(view === null) return;

    switch(view) {

      case AppViews.Topview:
      case AppViews.Main:
        if(!this.hideElement()) return;
        this.toggleEventTitle(true);
      break;


      case AppViews.Seatmap:
      case AppViews.Pano:
        if(this.hideElement()) return;
        this.toggleEventTitle(false);
      break;

    }

  });

  // METHODS

  /**
   * Función para ordenar los textos según el orden de las keys. 
   * Se utiliza junto a la pipe KeyValuePipe en el template para retornar
   * los textos en el orden deseado.
   * @param {KeyValue<k,v>} a 
   * @param {KeyValue<k,v>} b 
   * @returns 
   */
  public orderFn = (a: KeyValue<keyof Texts, string> , b: KeyValue<keyof Texts, string>): number => {

    const order: (keyof Texts)[] = ['top', 'main', 'bottom'];	

    return order.indexOf(a.key as keyof Texts) - order.indexOf(b.key as keyof Texts);

  }

  public formatDisplayText(value: string): string {
    return [...value].map((char: string): string =>
      PRESERVED_TEXT_CHARACTERS_SET.has(char)
        ? char
        : char.toUpperCase()
    ).join('');
  }

  /**
   * Muestra u oculta el event title. 
   * Si hideElement es true, se revierte a false para que se muestre el elemento.
   * Si show es false, hideElement se establece a true para ocultar el elemento.
   * @param show 
   */
  public toggleEventTitle(show: boolean): void {
    const eventTitle: HTMLDivElement | undefined = this.eventTitle?.nativeElement;
    if(!eventTitle) return;

    if(this.hideElement()) this.hideElement.set(false);

    anime({
      targets:      eventTitle,
      duration:     300, 
      easing:       'easeInOutQuad',
      translateY:   show ? 0 : -100,
      opacity:      show ? 1 : 0,
      complete:     (): void => { if(!show) this.hideElement.set(true); } 
    })

  }
  
}
